<script setup lang="ts">
import { computed, watch, ref, Ref, onMounted } from 'vue';
import { toolbarTimeOptions, timeLabels } from '@/components/Main/main.config';
import timeFormat from '@/utils/time/timeFormat';
import getLocalTime from '@/utils/time/localtime';
import RexDatePicker from '@/rex-components/rex-date-picker-v2.vue';
import { dateFormat } from '@/store/modules/global/settings/settings.getter';
import { useStoreData } from '@/components/Common/useStoreData';
import { disableDatePickerOption } from '@/components/Common/graph.config';

const emit = defineEmits(['onDurationChange']);

const { getTimeZoneOff, setIsForecastModalEnable, isForecastModalEnable, utc } =
  useStoreData();

const dateOptionsKey = ref(0);
const durationRow = ref({
  start: new Date(new Date().getTime() - 5 * 60000),
  end: new Date(),
  step: 'MINUTE',
});

const durationShow = ref({
  start: getLocalTime(getTimeZoneOff.value, durationRow.value.start),
  end: getLocalTime(getTimeZoneOff.value, durationRow.value.end),
  step: durationRow.value.step,
});

const currTimeLabel = ref('');

const duration = ref({
  start: getLocalTime(parseFloat(utc.value), durationRow.value.start),
  end: getLocalTime(parseFloat(utc.value), durationRow.value.end),
  step: durationRow.value.step,
});

const getDurationTime = ref({
  start: dateFormat(duration.value.start, duration.value.step),
  end: dateFormat(duration.value.end, duration.value.step),
  step: duration.value.step,
});

const timeSelectOptions = computed(() => {
  return toolbarTimeOptions.filter(
    (obj) => !disableDatePickerOption?.includes(obj.name),
  );
});

const time = computed({
  get() {
    return [durationShow.value?.start, durationShow.value?.end];
  },
  set(val) {
    durationRow.value = timeFormat(val);
  },
});

const getEndDuration = (endTime) => {
  return isForecastModalEnable.value
    ? new Date(endTime.getTime() + 6 * 60 * 60 * 1000)
    : endTime;
};

const selectedTime = computed(() => {
  if (timeSelectOptions.value.some((obj) => obj.name === currTimeLabel.value)) {
    return currTimeLabel.value;
  }
  return timeSelectOptions.value[timeSelectOptions.value.length - 1].name;
});

function onTimeChange(option) {
  const startDate = new Date();
  startDate.setTime(startDate.getTime() - option.value);
  const time = [startDate, new Date()];
  durationRow.value = timeFormat(time);
  currTimeLabel.value = option.name;
  if (
    isForecastModalEnable.value &&
    currTimeLabel.value !== timeLabels.oneHour
  ) {
    setIsForecastModalEnable(!isForecastModalEnable.value);
  }
}

function setCustomDuration(range) {
  durationRow.value = timeFormat(range);
  currTimeLabel.value = timeLabels.custom;
  setIsForecastModalEnable(false);
}

watch(timeSelectOptions, () => {
  dateOptionsKey.value += 1;
});
watch(isForecastModalEnable, (newVal, oldVal) => {
  if (newVal) {
    onTimeChange(toolbarTimeOptions[3]);
  }
  if (!newVal) {
    getDurationTime.value = {
      start: dateFormat(duration.value.start, duration.value.step),
      end: dateFormat(getEndDuration(duration.value.end), duration.value.step),
      step: duration.value.step,
    };
  }
});

watch(durationRow, () => {
  durationShow.value = {
    start: getLocalTime(getTimeZoneOff.value, durationRow.value.start),
    end: getLocalTime(getTimeZoneOff.value, durationRow.value.end),
    step: durationRow.value.step,
  };
  duration.value = {
    start: getLocalTime(parseFloat(utc.value), durationRow.value.start),
    end: getLocalTime(parseFloat(utc.value), durationRow.value.end),
    step: durationRow.value.step,
  };
});

watch(duration, () => {
  getDurationTime.value = {
    start: dateFormat(duration.value.start, duration.value.step),
    end: dateFormat(getEndDuration(duration.value.end), duration.value.step),
    step: duration.value.step,
  };
});

watch(getDurationTime, () => {
  emit('onDurationChange', getDurationTime.value);
});

onMounted(() => {
  if (isForecastModalEnable.value) {
    durationRow.value = {
      start: new Date(new Date().getTime() - 60 * 60000),
      end: new Date(),
      step: 'MINUTE',
    };
    currTimeLabel.value = timeLabels.oneHour;
  } else {
    durationRow.value = {
      start: new Date(new Date().getTime() - 5 * 60000),
      end: new Date(),
      step: 'MINUTE',
    };
    currTimeLabel.value = timeLabels.fiveMins;
  }
});
</script>

<template>
  <div>
    <RexDatePicker
      :options="timeSelectOptions"
      :enableIcon="true"
      :iconDetails="{
        iconName: 'calendarDuration',
        iconWidth: '1.125rem',
        iconHeight: '1.125rem',
      }"
      :duration="time"
      @input="onTimeChange"
      @custom="setCustomDuration"
      @disableAutoRef="true"
      :selectedTimeLabel="selectedTime"
      :key="dateOptionsKey"
      :disableTooltip="false"
      :tooltipContent="$t('forecastTooltip')"
      :enableWarning="true"
      :warningContent="$t('forecastDateWarning')"
      :customPicker="true"
    />
  </div>
</template>

<style lang="scss" scoped></style>
